import { useAppStore, type PendingAttachment } from '../stores/appStore';
import { formatFileSize, prepareImageForUpload } from './imageUtils';
import { buildApiUrl } from './serverOrigin';

export interface UploadedChatAttachment {
  path: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
}

export const MAX_CHAT_ATTACHMENTS = 10;
export const CHAT_UPLOAD_ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,.psd,.mp3,image/png,image/jpeg,image/gif,image/webp,image/vnd.adobe.photoshop,audio/mpeg,audio/mp3';

const MIME_BY_EXTENSION: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.psd': 'image/vnd.adobe.photoshop',
  '.mp3': 'audio/mpeg',
};

const SUPPORTED_EXTENSIONS = new Set(Object.keys(MIME_BY_EXTENSION));
const SUPPORTED_MIME_TYPES = new Set(Object.values(MIME_BY_EXTENSION));

function getExtension(fileName: string): string {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot >= 0 ? fileName.slice(lastDot).toLowerCase() : '';
}

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Failed to read file preview.'));
    reader.readAsDataURL(file);
  });
}

export function generateAttachmentId(): string {
  return crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function formatAttachmentSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (bytes >= 1024) {
    return `${Math.round(bytes / 1024)} KB`;
  }

  return `${bytes} B`;
}

export function normalizeUploadMimeType(file: Pick<File, 'name' | 'type'>): string {
  const explicitType = file.type?.trim().toLowerCase();
  if (explicitType && explicitType !== 'application/octet-stream') {
    return explicitType;
  }

  return MIME_BY_EXTENSION[getExtension(file.name)] ?? explicitType ?? 'application/octet-stream';
}

export function isSupportedChatUpload(file: Pick<File, 'name' | 'type'>): boolean {
  const extension = getExtension(file.name);
  const mimeType = normalizeUploadMimeType(file);
  return SUPPORTED_EXTENSIONS.has(extension) || SUPPORTED_MIME_TYPES.has(mimeType);
}

export async function createPendingAttachment(file: File): Promise<PendingAttachment> {
  if (!isSupportedChatUpload(file)) {
    throw new Error('Unsupported file type');
  }

  const sourceMimeType = normalizeUploadMimeType(file);
  const extension = getExtension(file.name);
  const isPreviewableImage = sourceMimeType.startsWith('image/') && extension !== '.psd';
  const isAudio = sourceMimeType.startsWith('audio/');

  let fileToAttach = file;
  let originalSizeBytes: number | undefined;
  let wasCompressed: boolean | undefined;
  let compressionSummary: string | undefined;

  if (isPreviewableImage) {
    const prepared = await prepareImageForUpload(file);
    fileToAttach = prepared.file;
    originalSizeBytes = prepared.originalSizeBytes;
    wasCompressed = prepared.wasCompressed;
    compressionSummary = prepared.wasCompressed
      ? `${formatFileSize(prepared.originalSizeBytes)} → ${formatFileSize(prepared.finalSizeBytes)}`
      : undefined;
  }

  const mimeType = normalizeUploadMimeType(fileToAttach);

  return {
    id: generateAttachmentId(),
    file: fileToAttach,
    mimeType,
    fileName: fileToAttach.name,
    dataUrl: isPreviewableImage ? await fileToDataUrl(fileToAttach) : undefined,
    sizeBytes: fileToAttach.size,
    previewKind: isPreviewableImage ? 'image' : isAudio ? 'audio' : 'file',
    originalSizeBytes,
    wasCompressed,
    compressionSummary,
  };
}

export async function uploadPendingAttachment(
  attachment: Pick<PendingAttachment, 'file' | 'fileName' | 'mimeType' | 'sizeBytes'>,
  gatewayToken: string,
): Promise<UploadedChatAttachment> {
  const response = await fetch(buildApiUrl('/api/uploads', useAppStore.getState().gatewayUrl), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${gatewayToken}`,
      'Content-Type': attachment.mimeType || 'application/octet-stream',
      'X-Upload-Filename': encodeURIComponent(attachment.fileName),
      'X-Upload-Mime-Type': attachment.mimeType || 'application/octet-stream',
    },
    body: attachment.file,
  });

  const data = (await response.json().catch(() => null)) as { path?: unknown; error?: unknown } | null;

  if (!response.ok) {
    const errorMessage = typeof data?.error === 'string'
      ? data.error
      : `Upload failed (${response.status})`;
    throw new Error(errorMessage);
  }

  if (!data || typeof data.path !== 'string' || !data.path.trim()) {
    throw new Error('Upload succeeded but no file path was returned.');
  }

  return {
    path: data.path,
    fileName: attachment.fileName,
    mimeType: attachment.mimeType,
    sizeBytes: attachment.sizeBytes,
  };
}

export async function uploadPendingAttachments(
  attachments: Array<Pick<PendingAttachment, 'file' | 'fileName' | 'mimeType' | 'sizeBytes'>>,
  gatewayToken: string,
): Promise<UploadedChatAttachment[]> {
  const uploaded: UploadedChatAttachment[] = [];

  for (const attachment of attachments) {
    uploaded.push(await uploadPendingAttachment(attachment, gatewayToken));
  }

  return uploaded;
}

export function formatUploadedAttachmentsForMessage(attachments: UploadedChatAttachment[]): string {
  if (attachments.length === 0) {
    return '';
  }

  const heading = attachments.length === 1 ? 'Attached file:' : 'Attached files:';
  const lines = attachments.map((attachment) => {
    const size = formatAttachmentSize(attachment.sizeBytes);
    return `- ${attachment.fileName} (${attachment.mimeType}, ${size}): ${attachment.path}`;
  });

  return `${heading}\n${lines.join('\n')}`;
}

export function appendUploadedAttachmentsToMessage(
  text: string,
  attachments: UploadedChatAttachment[],
): string {
  const trimmed = text.trim();
  if (attachments.length === 0) {
    return trimmed;
  }

  const attachmentBlock = formatUploadedAttachmentsForMessage(attachments);
  return trimmed ? `${trimmed}\n\n${attachmentBlock}` : attachmentBlock;
}
